import { useMemo } from 'react';
import { Autocomplete, Box, FormControl, FormHelperText, TextField, Typography } from '@mui/material';
import { Control, Controller } from 'react-hook-form';
import { useAdminUsersQuery } from '../../../api/gql';
import { useControlledTableModel } from '../../../hooks/useControlledTableModel';
import { useErrorMessageResolver } from '../../../localization/useErrorMessageResolver';

type UsersFormAutocompleteProps = {
  control: Control<any>;
  name: string;
  label: string;
  readonly?: boolean;
};

export const UsersFormAutocomplete = (props: UsersFormAutocompleteProps) => {
  const resolveErrorMessage = useErrorMessageResolver();
  const { debouncedQuery, handlers } = useControlledTableModel({
    pageSize: 25,
  });
  const { data, isLoading, isError } = useAdminUsersQuery({
    query: debouncedQuery,
    skip: 0,
    take: 25,
  });

  const options = useMemo(
    () =>
      data?.adminUsers.results.map((user) => ({
        id: user.id,
        label: `${user.firstName} ${user.lastName}`,
        businessName: user.businessName,
        email: user.email,
      })),
    [data],
  );

  if (isError) {
    return <Typography>Data o uživatelých se nepodařio načíst</Typography>;
  }

  return (
    <Controller
      control={props.control}
      render={({ field, fieldState }) => (
        <FormControl fullWidth error={!!fieldState.error}>
          <Autocomplete
            loading={isLoading}
            {...field}
            readOnly={props.readonly}
            onChange={(e, v) => field.onChange(v?.id)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={props.label}
                error={!!fieldState.error}
                variant={props.readonly ? 'standard' : 'outlined'}
                InputProps={{
                  ...params.InputProps,
                  readOnly: props.readonly,
                  disableUnderline: props.readonly,
                }}
                fullWidth
              />
            )}
            renderOption={(props, option) => (
              <Box {...props} component={'li'} key={option.id}>
                <Typography whiteSpace={'nowrap'}>{option.label}</Typography>
                <Typography
                  color={'text.secondary'}
                  variant={'caption'}
                  ml={1}
                >{`${option.businessName} - ${option.email}`}</Typography>
              </Box>
            )}
            onInputChange={(e, value) => handlers.setQuery(value)}
            options={options ?? []}
            isOptionEqualToValue={(option, value) => {
              if (typeof value === 'string') {
                return option.id === value;
              }
              return option.id === value.id;
            }}
            filterOptions={(x) => x}
          />
          {fieldState.error?.message ? (
            <FormHelperText>{resolveErrorMessage(fieldState.error.message)}</FormHelperText>
          ) : null}
        </FormControl>
      )}
      name={props.name}
    />
  );
};
